import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminArticles, myPosts } from "../lib/api";
import { PenLine, Mic, Camera, Trophy, ChevronRight, Clock, CheckCircle2, AudioLines } from "lucide-react";

const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };

export default function ReporterHome({ user }) {
  const canWrite = ["reporter", "admin"].includes(user?.role);
  const [stories, setStories] = useState([]);
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    if (canWrite) adminArticles().then((a) => setStories(a.slice(0, 5))).catch(() => {});
    myPosts().then((p) => setPosts(p.slice(0, 5))).catch(() => {});
  }, [canWrite]);

  const actions = [
    canWrite && { to: "/admin/articles?new=1", icon: PenLine, title: "Write a story", sub: "Type or talk it through", tone: "bg-[var(--navy)] text-white" },
    canWrite && { to: "/admin/interviews", icon: AudioLines, title: "Record an interview", sub: "With permission · typed transcript afterwards", tone: "bg-white text-[var(--navy)]" },
    canWrite && { to: "/admin/galleries", icon: Camera, title: "Upload photos", sub: "Straight from your camera roll", tone: "bg-white text-[var(--navy)]" },
    { to: "/admin/submit", icon: user?.role === "coach" ? Trophy : Mic, title: user?.role === "coach" ? "File a game report" : "Submit to editor", sub: "Goes to an editor for approval", tone: "bg-white text-[var(--navy)]" },
  ].filter(Boolean);

  return (
    <div data-testid="reporter-home" className="mx-auto max-w-2xl">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--gold)]">{new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</p>
      <h2 className="font-display mt-1 text-3xl font-bold text-[var(--navy)]" data-testid="reporter-greeting">{greeting()}, {user?.name?.split(" ")[0]}</h2>
      <p className="mt-1 text-sm text-slate-500">What are you filing today?</p>

      <div className="mt-5 grid gap-3">
        {actions.map((a, i) => (
          <Link key={a.title} to={a.to} data-testid={`reporter-action-${i}`} className={`flex items-center gap-4 rounded-2xl border border-slate-200 p-4 shadow-sm transition-transform active:scale-[0.98] ${a.tone}`}>
            <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${i === 0 ? "bg-[var(--gold)] text-white" : "bg-[var(--gold)]/15 text-[var(--gold)]"}`}><a.icon size={22} /></span>
            <div className="min-w-0 flex-1"><p className="font-bold">{a.title}</p><p className={`text-xs ${i === 0 ? "text-white/70" : "text-slate-500"}`}>{a.sub}</p></div>
            <ChevronRight size={18} className="opacity-40" />
          </Link>
        ))}
      </div>

      {canWrite && <StoryList stories={stories} />}
      {posts.length > 0 && <PostList posts={posts} />}
    </div>
  );
}

function StoryList({ stories }) {
  return (
    <section className="mt-8">
      <div className="mb-2 flex items-center justify-between"><h3 className="font-bold text-[var(--navy)]">My stories</h3><Link to="/admin/articles" className="text-xs font-semibold text-[var(--gold)]">See all</Link></div>
      <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {stories.map((s) => (
          <Link key={s.id} to={`/admin/articles?edit=${s.id}`} data-testid={`reporter-story-${s.id}`} className="flex items-center gap-3 p-3">
            {s.image ? <img src={s.image} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" /> : <span className="h-12 w-12 shrink-0 rounded-lg bg-slate-100" />}
            <div className="min-w-0"><p className="line-clamp-2 text-sm font-semibold leading-snug text-[var(--navy)]">{s.title}</p><p className="text-[11px] capitalize text-slate-400">{s.category} · {s.author_name}</p></div>
          </Link>
        ))}
        {stories.length === 0 && <p className="p-4 text-sm text-slate-400">You haven't filed a story yet. Tap "Write a story" to start.</p>}
      </div>
    </section>
  );
}

function PostList({ posts }) {
  return (
    <section className="mt-8">
      <h3 className="mb-2 font-bold text-[var(--navy)]">My submissions</h3>
      <div className="divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-3 p-3">
            <p className="line-clamp-1 text-sm font-semibold text-[var(--navy)]">{p.title}</p>
            {p.status === "pending"
              ? <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700"><Clock size={11} /> With editor</span>
              : <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700"><CheckCircle2 size={11} /> Published</span>}
          </div>
        ))}
      </div>
    </section>
  );
}
